import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Standardized Badge Component
 * 
 * Unified badge component for all badge use cases across the platform.
 * Uses centralized status color system for consistent visual appearance.
 * 
 * @example
 * // Status badges
 * <Badge variant="success">Active</Badge>
 * <Badge variant="error">Failed</Badge>
 * <Badge variant="warning">Pending</Badge>
 * 
 * @example
 * // With custom styling
 * <Badge variant="info" size="lg" className="font-bold">New</Badge>
 * 
 * @example
 * // Outline style
 * <Badge variant="outline">Secondary</Badge>
 */
const badgeVariants = cva(
  'inline-flex items-center rounded-full border font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        // Status variants using unified color system
        success: 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100',
        error: 'border-orange-200 bg-orange-50 text-orange-800 hover:bg-orange-100',
        warning: 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100',
        info: 'border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100',
        neutral: 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100',
        
        // Legacy variants (mapped to status variants)
        default: 'border-primary/20 bg-primary text-primary-foreground hover:bg-primary/80',
        primary: 'border-primary-500/20 bg-primary-500 text-white hover:bg-primary-600',
        secondary: 'border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200',
        destructive: 'border-red-200 bg-red-100 text-red-800 hover:bg-red-200',
        outline: 'border-slate-300 text-slate-700 bg-white hover:bg-slate-50',
        
        // Integration-specific variants (mapped to success)
        installed: 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100',
        enabled: 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100',
        
        // High-priority badges (Named Account, Customer, High Intent, Executive Level)
        priority: 'border-slate-900 bg-slate-900 text-white hover:bg-slate-800',
        // Prospect badges (medium priority)
        prospect: 'border-slate-700 bg-slate-700 text-white hover:bg-slate-600',
      },
      size: {
        xs: 'px-1.5 py-0.5 text-[10px]',
        sm: 'px-2 py-0.5 text-xs',
        default: 'px-2.5 py-0.5 text-xs',
        md: 'px-2.5 py-1 text-sm',
        lg: 'px-3 py-1 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * Badge content
   */
  children: React.ReactNode;
  
  /**
   * Optional icon to display before text
   */
  icon?: React.ComponentType<{ className?: string }>;
  
  /**
   * Icon size (defaults to match text size)
   */
  iconSize?: string;
}

/**
 * Badge Component
 * 
 * Standardized badge component for all badge use cases.
 * Supports status variants, sizes, and optional icons.
 */
function Badge({ 
  className, 
  variant, 
  size, 
  icon: Icon,
  iconSize,
  children,
  onClick,
  onKeyDown,
  role,
  tabIndex,
  ...props 
}: BadgeProps) {
  const getIconSize = () => {
    if (iconSize) return iconSize;
    switch (size) {
      case 'xs': return 'h-3 w-3';
      case 'sm': return 'h-3 w-3';
      case 'md': return 'h-4 w-4';
      case 'lg': return 'h-4 w-4';
      default: return 'h-3.5 w-3.5';
    }
  };

  // Determine if badge is interactive
  const isInteractive = Boolean(onClick);
  const computedRole = role || (isInteractive ? 'button' : undefined);
  const computedTabIndex = tabIndex !== undefined ? tabIndex : (isInteractive ? 0 : undefined);

  // Handle keyboard interaction for interactive badges
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      // Create a synthetic mouse event for onClick handler
      const syntheticEvent = {
        ...e,
        currentTarget: e.currentTarget,
        target: e.target,
      } as unknown as React.MouseEvent<HTMLDivElement>;
      onClick?.(syntheticEvent);
    }
    onKeyDown?.(e);
  };

  return (
    <div 
      className={cn(
        badgeVariants({ variant, size }),
        isInteractive && 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        className
      )}
      role={computedRole}
      tabIndex={computedTabIndex}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {Icon && <Icon className={cn(getIconSize(), 'mr-1')} />}
      {children}
    </div>
  );
}

export { Badge, badgeVariants };
